import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Field, Form } from 'react-final-form';
import { Button, Loader } from 'ncoded-component-library';
import arrayMutators from 'final-form-arrays';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import GroupAccess from './components/GroupAccess';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import FileInfo from './components/FileInfo';
import FileSecurity from './components/FileSecurity';
import { DataRoomDocumentHistory } from './components/DataRoomDocumentHistory';
import { BulkAvailabilityMembersForm } from 'router/subrouters/DataRoom/models/DataRoomBulk';
import { FieldArray } from 'react-final-form-arrays';
import {
  prepareFormData,
  prepareResponseForFormData,
  processDataForUpdatedValues,
} from './utils';
import AllBuySidesGroupAccess from './components/AllBuySidesGroupAccess';
import ExpandIcon from 'icons/Expand.icon';
import useMultiCallbackRef from 'hooks/useMultiCallbackRef';
import { CollapsibleRef } from 'ncoded-component-library/build/components/molecules/Collapsible/Collapsible.component';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import { getCanShareNode, getIsRevisedVersion } from 'utils';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import CheckToggleField from 'components/CheckToggleField';

import './ManageFileAccess.styles.scss';

type ManageFileAccessProps = {
  className?: string;
  nodes: DataRoomNode[];
  parentId: string;
  updateNodes: (nodes: DataRoomNode[]) => void;
};

const ManageFileAccess: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ManageFileAccessProps
> = (props, ref) => {
  const { className, nodes, parentId, updateNodes } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [nodesForSubmit, setNodesForSubmit] = useState<Partial<DataRoomNode>[]>(
    [],
  );

  const fileIdsForSubmit = useMemo(
    () => nodesForSubmit.map(({ _id }) => _id),
    [nodesForSubmit],
  );

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  const { sellSideMembers, buySideGroups } = useContext(MembersContext);
  const {
    dataRoom: { accessConfirmationDays },
  } = useContext(DataRoomContext);

  const [groupsCollapsible, groupsCollapsibleRef] =
    useMultiCallbackRef<CollapsibleRef>();

  const classes = classNames('manage-file-access', className);

  const isSelectedOneFile = nodes?.length === 1;
  const firstElement = nodes[0];

  const haveFileHistory =
    isSelectedOneFile && getIsRevisedVersion(firstElement.version);

  const hasBuySideGroups = !!buySideGroups?.length;

  const hasMoreBuySideGroups = useMemo(
    () => hasBuySideGroups && buySideGroups?.length > 1,
    [hasBuySideGroups, buySideGroups?.length],
  );

  const areAllBuySideGroupsOpen = useMemo(
    () => groupsCollapsible?.every((group) => group?.open),
    [groupsCollapsible],
  );

  const fileIds = useMemo(() => nodes.map(({ _id }) => _id), [nodes]);

  const formData = useMemo(
    () =>
      nodesForSubmit?.length &&
      prepareFormData(
        prepareResponseForFormData({
          sellSideMembers,
          buySideGroups,
          accessConfirmationDays,
          dataRoomNodes: nodesForSubmit,
        }),
      ),
    [nodesForSubmit, buySideGroups, sellSideMembers, accessConfirmationDays],
  );

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { t } = useTranslation();

  const collapseBuySideGroups = useCallback(
    (e) => {
      e.stopPropagation();
      groupsCollapsible?.map((group) =>
        group.setOpen(!areAllBuySideGroupsOpen),
      );
    },
    [areAllBuySideGroupsOpen, groupsCollapsible],
  );

  const handleOnSubmit = useCallback(
    async (values: BulkAvailabilityMembersForm) => {
      try {
        const newData = processDataForUpdatedValues(values, formData);

        const { data } = await dataRoomApi.nodes.bulkUpdateManageDetails(
          fileIdsForSubmit,
          newData,
          parentId,
        );

        popSuccess(t('successfullyUpdatedSettings', { count: nodes?.length }));
        updateNodes?.(data);
        drawer?.setOpen(false);
      } catch (e) {
        popServerError(e);
      }
    },
    [
      parentId,
      nodes,
      fileIdsForSubmit,
      formData,
      drawer,
      updateNodes,
      popSuccess,
      t,
      popServerError,
    ],
  );

  useEffect(() => {
    if (!drawer?.open) return;

    (async () => {
      try {
        setIsLoading(true);
        const { data } = await dataRoomApi.nodes.bulkGetManageDetails(fileIds);

        setNodesForSubmit(data.dataRoomNodes.filter(getCanShareNode));
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [drawer?.open, fileIds, popServerError]);

  return (
    <SideDrawer
      ref={drawerRef}
      className={classes}
      closeAction={() => setNodesForSubmit([])}
    >
      <p className="drawer-title">{t('manageAccess')}</p>

      <FileInfo nodes={nodes} />

      {isLoading ? (
        <Loader />
      ) : (
        <Form
          mutators={{ ...arrayMutators }}
          initialValues={formData}
          onSubmit={handleOnSubmit}
          render={({
            submitting,
            pristine,
            hasValidationErrors,
            handleSubmit,
          }) => {
            const disabledSubmit =
              submitting || pristine || hasValidationErrors;

            return (
              <form onSubmit={handleSubmit}>
                {submitting && <Loader />}

                <div className="manage-file-access__soft-signals white-panel">
                  <p className="title">{t('softSignals')}</p>

                  <Field
                    name="allowSoftSignals.checked"
                    component={CheckToggleField}
                    type="checkbox"
                    label={t('allowSoftSignals')}
                  />
                </div>

                <FileSecurity />

                {hasBuySideGroups && (
                  <div className="manage-file-access__buy-groups">
                    <div>
                      <p>{t('allBuyerGroups')}</p>

                      {hasMoreBuySideGroups && (
                        <Button
                          icon={ExpandIcon}
                          variant="link"
                          iconPosition="right"
                          onClick={collapseBuySideGroups}
                        >
                          {t(
                            areAllBuySideGroupsOpen
                              ? 'collapseAllGroups'
                              : 'expandAllGroups',
                          )}
                        </Button>
                      )}
                    </div>

                    <AllBuySidesGroupAccess />

                    <FieldArray name="buySideGroups">
                      {({ fields }) => (
                        <>
                          {fields.map((name, index) => {
                            const { name: groupName, isSingleMemberGroup } =
                              fields.value[index] || {};

                            return (
                              <GroupAccess
                                key={name}
                                name={`${name}.dataRoomMembers`}
                                title={
                                  isSingleMemberGroup ? t(groupName) : groupName
                                }
                                ref={groupsCollapsibleRef(index)}
                              />
                            );
                          })}
                        </>
                      )}
                    </FieldArray>
                  </div>
                )}

                <GroupAccess
                  name="sellSideMembers"
                  title={t('sellSideMembers')}
                  hasFileAccess={false}
                />

                {haveFileHistory && (
                  <DataRoomDocumentHistory
                    node={firstElement}
                    canFetch={drawer?.open}
                  />
                )}

                <p className="notify">{t('updateFileAccessNotify')}</p>

                <div className="manage-file-access__actions">
                  <Button variant="link" onClick={() => drawer?.setOpen(false)}>
                    {t('cancel')}
                  </Button>
                  <Button type="submit" disabled={disabledSubmit}>
                    {t('save')}
                  </Button>
                </div>
              </form>
            );
          }}
        />
      )}
    </SideDrawer>
  );
};

export default forwardRef(ManageFileAccess);
